import React, { createContext, useContext, useEffect, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { firestore } from "../../../configs/FirebaseConfig";
import { FIRESTORE_PRICE_LISTS_TABLE, FIRESTORE_REGIONS_TABLE } from "../../../constants/FirestoreConstant";
import { COMPANY_COUNTRY_CODE } from "../../../constants/CompanyConstants";
import { message } from "antd";

// Create context
const UsePriceLists = createContext();

// Provider component
export const PriceListsProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [priceLists, setPriceLists] = useState([]);
  const [error, setError] = useState(null);
  const [listener, setListener] = useState(null);

  // Set up listener when the provider mounts
  useEffect(() => {
    setupListener();

    // Cleanup listener when unmounting
    return () => {
      if (listener) {
        listener();
      }
    };
  }, []);

  const setupListener = () => {
    setIsLoading(true);
    setError(null);

    try {
      const priceListsQuery = collection(
        firestore,
        `${FIRESTORE_REGIONS_TABLE}/${COMPANY_COUNTRY_CODE}/${FIRESTORE_PRICE_LISTS_TABLE}`,
      );

      const unsubscribe = onSnapshot(
        priceListsQuery,
        (snapshot) => {
          try {
            const priceListsData = snapshot.docs.map((doc) => ({
              ...doc.data(),
              id: doc.id,
            }));
            setPriceLists(priceListsData);
            setError(null);
            setIsLoading(false);
          } catch (err) {
            console.error("Error processing price lists snapshot:", err);
            setError(err.message);
            setIsLoading(false);
            message.error("Failed to process price lists data");
          }
        },
        (error) => {
          console.error("Error listening to price lists:", error);
          setError(error.message);
          setIsLoading(false);
          message.error("Failed to fetch price lists data");
        },
      );

      setListener(() => unsubscribe);
    } catch (error) {
      console.error("Error setting up price lists listener:", error);
      setError(error.message);
      setIsLoading(false);
      message.error("Failed to setup price lists listener");
    }
  };

  // Helper functions
  const getPriceListById = (id) => {
    return priceLists.find((priceList) => priceList.id === id);
  };

  const getCustomPriceForProduct = (priceListId, productId) => {
    const priceList = getPriceListById(priceListId);
    if (!priceList || !priceList.customPrices) return null;
    return priceList.customPrices[productId] || null;
  };

  return (
    <UsePriceLists.Provider
      value={{
        isLoading,
        priceLists,
        error,
        getPriceListById,
        getCustomPriceForProduct,
      }}
    >
      {children}
    </UsePriceLists.Provider>
  );
};

// Custom hook to use the price lists context
export const usePriceLists = () => {
  const context = useContext(UsePriceLists);

  if (context === undefined) {
    throw new Error("usePriceLists must be used within a PriceListsProvider");
  }

  return context;
};
